import { Link } from 'wouter';
import { usePrefs } from '../../context/PrefsContext.js';
import { THEME_PRESETS } from '../../themes/index.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { useT } from '../../i18n/index.js';

export default function SettingsPage() {
  const { prefs, setPrefs } = usePrefs();
  const t = useT();

  return (
    <div className="max-w-3xl mx-auto p-4 flex flex-col gap-8">
      <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
        {t('settings.title')}
      </h1>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm uppercase tracking-wide" style={{ color: 'var(--ac-text-muted)' }}>
          {t('settings.theme')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {THEME_PRESETS.map((p) => {
            const active = prefs.theme === p.name;
            return (
              <button
                key={p.name}
                type="button"
                onClick={() => void setPrefs({ theme: p.name })}
                className="p-3 rounded-xl border text-left flex items-center gap-3"
                style={{
                  backgroundColor: 'var(--ac-surface)',
                  borderColor: active ? 'var(--ac-accent)' : 'var(--ac-border)',
                  borderWidth: 2,
                }}
              >
                <span
                  className="w-10 h-10 rounded-lg border flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${p.previewColors.bg} 50%, ${p.previewColors.accent} 50%)`,
                    borderColor: 'var(--ac-border)',
                  }}
                />
                <div>
                  <div className="font-medium" style={{ color: 'var(--ac-text)' }}>
                    {p.label}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--ac-text-muted)' }}>
                    {p.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm uppercase tracking-wide" style={{ color: 'var(--ac-text-muted)' }}>
          {t('settings.theme_mode')}
        </h2>
        <div className="flex gap-2">
          {(['light', 'dark', 'system'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => void setPrefs({ themeMode: m })}
              className="px-4 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: prefs.themeMode === m ? 'var(--ac-accent)' : 'var(--ac-surface)',
                color: prefs.themeMode === m ? 'white' : 'var(--ac-text)',
                borderColor: 'var(--ac-border)',
              }}
            >
              {t(`settings.${m}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm uppercase tracking-wide" style={{ color: 'var(--ac-text-muted)' }}>
          {t('settings.locale')}
        </h2>
        <select
          value={prefs.locale}
          onChange={(e) => void setPrefs({ locale: e.target.value })}
          className="p-2 rounded-lg border text-sm w-full max-w-xs"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text)',
          }}
        >
          <option value="en-US">English (US)</option>
          <option value="de-DE">Deutsch</option>
        </select>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm uppercase tracking-wide" style={{ color: 'var(--ac-text-muted)' }}>
          {t('settings.accessibility')}
        </h2>
        <Toggle
          checked={prefs.reducedMotion}
          onChange={(v) => void setPrefs({ reducedMotion: v })}
          label={t('settings.reduced_motion')}
        />
        <Toggle
          checked={prefs.largeText}
          onChange={(v) => void setPrefs({ largeText: v })}
          label={t('settings.large_text')}
        />
        <Toggle
          checked={prefs.highContrast}
          onChange={(v) => void setPrefs({ highContrast: v })}
          label={t('settings.high_contrast')}
        />
      </section>

      <section className="flex flex-col gap-2">
        <Link
          href="/settings/homes"
          className="p-3 rounded-lg border text-sm"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text)',
          }}
        >
          {t('settings.homes')} →
        </Link>
        <Link
          href="/settings/account"
          className="p-3 rounded-lg border text-sm"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text)',
          }}
        >
          {t('settings.account')} →
        </Link>
        <Link
          href="/settings/audit"
          className="p-3 rounded-lg border text-sm"
          style={{
            backgroundColor: 'var(--ac-surface)',
            borderColor: 'var(--ac-border)',
            color: 'var(--ac-text)',
          }}
        >
          Audit log →
        </Link>
      </section>
    </div>
  );
}
